/**
 * TransporterStation route config modal — choose source and destination buildings
 * for a TransporterStation puppet to shuttle between.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BuildingKind, BuildingState, ResourceKind } from './client_message_protocol';
import { GameWebSocketClient } from './network_websocket_client';
import { GameClientWorldState } from './game_client_world_state';

const BUILDING_NAMES: Record<BuildingKind, string> = {
  [BuildingKind.SpiritRoot]: '灵根',
  [BuildingKind.GatheringArray]: '采集阵',
  [BuildingKind.RefiningArray]: '精炼阵',
  [BuildingKind.AlchemyFurnace]: '炼丹炉',
  [BuildingKind.Forge]: '锻造台',
  [BuildingKind.PuppetWorkshop]: '傀儡工坊',
  [BuildingKind.DefenseTower]: '防御塔',
  [BuildingKind.SwordTower]: '剑塔',
  [BuildingKind.SlowTrap]: '缓速陷阱',
  [BuildingKind.PuppetBarracks]: '傀儡兵营',
  [BuildingKind.TransporterStation]: '搬运站',
  [BuildingKind.HerbFarm]: '药田',
};

interface BuildingProdConfig {
  outputItem: ResourceKind | null;
  inputItem: ResourceKind | null;
}

const BUILDING_PROD_CONFIG: Partial<Record<BuildingKind, BuildingProdConfig>> = {
  [BuildingKind.SpiritRoot]: { outputItem: ResourceKind.SpiritQi, inputItem: null },
  [BuildingKind.GatheringArray]: { outputItem: ResourceKind.SpiritStone, inputItem: ResourceKind.SpiritQi },
  [BuildingKind.HerbFarm]: { outputItem: ResourceKind.Herb, inputItem: null },
  [BuildingKind.RefiningArray]: { outputItem: ResourceKind.SpiritQi, inputItem: null },
  [BuildingKind.AlchemyFurnace]: { outputItem: ResourceKind.Pill, inputItem: ResourceKind.SpiritStone },
  [BuildingKind.Forge]: { outputItem: ResourceKind.SpiritQi, inputItem: null },
};

const MODAL_W = 480;
const MODAL_H = 420;
const BG_COLOR = 0x111122;
const BG_ALPHA = 0.95;
const TEXT_COLOR = 0xdddddd;
const ACCENT_COLOR = 0x44ff44;
const SLOT_COLOR = 0x222244;
const SLOT_ACTIVE_COLOR = 0x335533;

export class UiTransporterRouteConfig {
  private container: Container;
  private _visible = false;
  private wsClient: GameWebSocketClient;
  private worldState: GameClientWorldState;
  private stationId: number;
  private sourceId: number | null = null;
  private destId: number | null = null;
  private slotMode: 'source' | 'dest' = 'source'; // which slot we're currently filling
  private screenW: number;
  private screenH: number;
  private bg: Graphics;
  private titleText: Text;
  private listContainer: Container;
  private buildingEntries: Container[] = [];
  private sourceSlot: Container;
  private sourceSlotText: Text;
  private destSlot: Container;
  private destSlotText: Text;
  private confirmBtn: Container;
  private cancelBtn: Container;

  constructor(
    uiLayer: Container,
    screenW: number,
    screenH: number,
    wsClient: GameWebSocketClient,
    worldState: GameClientWorldState,
    stationId: number,
  ) {
    this.wsClient = wsClient;
    this.worldState = worldState;
    this.stationId = stationId;
    this.screenW = screenW;
    this.screenH = screenH;

    this.container = new Container();
    this.container.visible = false;

    const bodyStyle = new TextStyle({ fontFamily: 'monospace', fontSize: 12, fill: TEXT_COLOR });
    const titleStyle = new TextStyle({ fontFamily: 'monospace', fontSize: 16, fill: ACCENT_COLOR, fontWeight: 'bold' });
    const smallStyle = new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: TEXT_COLOR });

    // Background
    this.bg = new Graphics();
    this.bg.eventMode = 'static';
    this.bg.on('pointerdown', (event) => { event.stopPropagation(); });
    this.container.addChild(this.bg);

    // Title
    this.titleText = new Text({ text: '搬运站 — 配置路线', style: titleStyle, resolution: 2, roundPixels: true });
    this.container.addChild(this.titleText);

    // Source slot
    this.sourceSlot = this.createSlot('起点 (Source)');
    this.container.addChild(this.sourceSlot);

    this.sourceSlotText = new Text({ text: '点击建筑选择', style: smallStyle, resolution: 2, roundPixels: true });
    this.sourceSlot.addChild(this.sourceSlotText);

    // Dest slot
    this.destSlot = this.createSlot('终点 (Destination)');
    this.container.addChild(this.destSlot);

    this.destSlotText = new Text({ text: '点击建筑选择', style: smallStyle, resolution: 2, roundPixels: true });
    this.destSlot.addChild(this.destSlotText);

    // Scrollable building list
    this.listContainer = new Container();
    this.container.addChild(this.listContainer);

    // Confirm button
    this.confirmBtn = this.createButton('确认', 0x226622, 0x44ff44);
    this.confirmBtn.on('pointertap', () => this.onConfirm());
    this.container.addChild(this.confirmBtn);

    // Cancel button
    this.cancelBtn = this.createButton('取消', 0x442222, 0xff4444);
    this.cancelBtn.on('pointertap', () => this.hide());
    this.container.addChild(this.cancelBtn);

    uiLayer.addChild(this.container);
  }

  private createSlot(label: string): Container {
    const slot = new Container();
    const bg = new Graphics();
    bg.roundRect(0, 0, MODAL_W - 60, 32, 4);
    bg.fill({ color: SLOT_COLOR, alpha: 1 });
    slot.addChild(bg);
    const labelText = new Text({
      text: label,
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 10, fill: 0x888888 }),
      resolution: 2,
      roundPixels: true,
    });
    slot.addChild(labelText);
    return slot;
  }

  private createButton(label: string, bgColor: number, textColor: number): Container {
    const btn = new Container();
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    const bg = new Graphics();
    bg.roundRect(0, 0, 100, 28, 4);
    bg.fill({ color: bgColor, alpha: 1 });
    btn.addChild(bg);
    const labelText = new Text({
      text: label,
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 12, fill: textColor }),
      resolution: 2,
      roundPixels: true,
    });
    labelText.anchor.set(0.5, 0.5);
    labelText.x = 50;
    labelText.y = 14;
    btn.addChild(labelText);
    return btn;
  }

  show(): void {
    this.sourceId = null;
    this.destId = null;
    this.slotMode = 'source';
    this._visible = true;
    this.container.visible = true;
    this.drawLayout();
    this.refreshList();
  }

  hide(): void {
    this._visible = false;
    this.container.visible = false;
  }

  isVisible(): boolean {
    return this._visible;
  }

  private drawLayout(): void {
    const cx = (this.screenW - MODAL_W) / 2;
    const cy = (this.screenH - MODAL_H) / 2;

    this.bg.clear();
    this.bg.roundRect(cx, cy, MODAL_W, MODAL_H, 8);
    this.bg.fill({ color: BG_COLOR, alpha: BG_ALPHA });
    this.bg.roundRect(cx + 1, cy + 1, MODAL_W - 2, MODAL_H - 2, 7);
    this.bg.stroke({ color: 0x555555, width: 1, alpha: 0.6 });

    this.titleText.x = cx + 20;
    this.titleText.y = cy + 16;

    // Source slot
    this.sourceSlot.x = cx + 30;
    this.sourceSlot.y = cy + 50;

    // Dest slot
    this.destSlot.x = cx + 30;
    this.destSlot.y = cy + 95;

    // Building list
    this.listContainer.x = cx + 30;
    this.listContainer.y = cy + 145;

    // Buttons
    this.confirmBtn.x = cx + MODAL_W - 250;
    this.confirmBtn.y = cy + MODAL_H - 42;
    this.cancelBtn.x = cx + MODAL_W - 140;
    this.cancelBtn.y = cy + MODAL_H - 42;
  }

  private refreshList(): void {
    // Clear old entries
    for (const entry of this.buildingEntries) {
      this.listContainer.removeChild(entry);
      entry.destroy({ children: true });
    }
    this.buildingEntries = [];

    const buildings = Array.from(this.worldState.buildings.values())
      .filter(b => b.id !== this.stationId && b.player_id === this.worldState.localPlayerId);

    // Filter buildings by current slot mode
    let filteredBuildings: typeof buildings;
    if (this.slotMode === 'source') {
      filteredBuildings = buildings.filter(b => {
        const cfg = BUILDING_PROD_CONFIG[b.kind];
        return cfg !== undefined && cfg.outputItem !== null;
      });
    } else {
      // Dest mode: only show consumers matching the source's output
      const sourceBld = this.sourceId !== null ? this.worldState.buildings.get(this.sourceId) : null;
      const sourceCfg = sourceBld ? BUILDING_PROD_CONFIG[sourceBld.kind] : undefined;
      const sourceOutput = sourceCfg?.outputItem ?? null;
      if (sourceOutput === null) {
        filteredBuildings = [];
      } else {
        filteredBuildings = buildings.filter(b => {
          const cfg = BUILDING_PROD_CONFIG[b.kind];
          return cfg !== undefined && cfg.inputItem === sourceOutput;
        });
      }
    }

    // No-match message for empty dest list
    if (filteredBuildings.length === 0) {
      const msgText = this.slotMode === 'dest'
        ? '没有可匹配的消耗建筑'
        : '没有可生产灵气的建筑';
      const noMatch = new Text({
        text: msgText,
        style: new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: 0x888888 }),
        resolution: 2,
        roundPixels: true,
      });
      noMatch.x = 8;
      noMatch.y = 4;
      this.listContainer.addChild(noMatch);
      this.buildingEntries.push(noMatch);
      return;
    }

    const entryStyle = new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: TEXT_COLOR });
    const ownedStyle = new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: 0xaaaaaa });

    let yOffset = 0;
    for (const b of filteredBuildings) {
      const entry = new Container();
      entry.eventMode = 'static';
      entry.cursor = 'pointer';

      const name = BUILDING_NAMES[b.kind] ?? b.kind;
      const posStr = `(${Math.round(b.position.x)}, ${Math.round(b.position.y)})`;
      const isOwned = this.worldState.localPlayerId !== null &&
        this.worldState.players.some(p => p.id === this.worldState.localPlayerId);

      // Append resource info
      const cfg = BUILDING_PROD_CONFIG[b.kind];
      let resStr = '';
      if (this.slotMode === 'source' && cfg?.outputItem) {
        resStr = ` →${cfg.outputItem}`;
      } else if (this.slotMode === 'dest' && cfg?.inputItem) {
        resStr = ` ←${cfg.inputItem}`;
      }

      const textStr = `${name}${resStr}  ${posStr}`;
      const useStyle = isOwned ? entryStyle : ownedStyle;

      const text = new Text({ text: textStr, style: useStyle, resolution: 2, roundPixels: true });
      text.x = 8;
      text.y = 4;
      entry.addChild(text);

      // Background highlight for selected slots
      const bg = new Graphics();
      if (b.id === this.sourceId) {
        bg.roundRect(0, 0, MODAL_W - 60, 26, 4);
        bg.fill({ color: 0x224422, alpha: 0.6 });
        entry.addChildAt(bg, 0);
      } else if (b.id === this.destId) {
        bg.roundRect(0, 0, MODAL_W - 60, 26, 4);
        bg.fill({ color: 0x442222, alpha: 0.6 });
        entry.addChildAt(bg, 0);
      }

      entry.y = yOffset;
      const bid = b.id;
      entry.on('pointertap', () => {
        if (this.slotMode === 'source') {
          this.sourceId = bid;
          this.slotMode = 'dest';
        } else {
          this.destId = bid;
          this.slotMode = 'source';
        }
        this.refreshSlotTexts();
        this.refreshList();
      });

      this.listContainer.addChild(entry);
      this.buildingEntries.push(entry);
      yOffset += 28;
    }
  }

  private refreshSlotTexts(): void {
    const smallStyle = new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: TEXT_COLOR });
    if (this.sourceId !== null) {
      const b = this.worldState.buildings.get(this.sourceId);
      if (b) {
        const name = BUILDING_NAMES[b.kind] ?? b.kind;
        this.sourceSlotText.text = `${name} (${Math.round(b.position.x)},${Math.round(b.position.y)})`;
        this.sourceSlotText.style.fill = ACCENT_COLOR;
      }
    } else {
      this.sourceSlotText.text = '点击建筑选择';
      this.sourceSlotText.style.fill = 0x888888;
    }
    if (this.destId !== null) {
      const b = this.worldState.buildings.get(this.destId);
      if (b) {
        const name = BUILDING_NAMES[b.kind] ?? b.kind;
        this.destSlotText.text = `${name} (${Math.round(b.position.x)},${Math.round(b.position.y)})`;
        this.destSlotText.style.fill = ACCENT_COLOR;
      }
    } else {
      this.destSlotText.text = '点击建筑选择';
      this.destSlotText.style.fill = 0x888888;
    }
  }

  private onConfirm(): void {
    if (this.sourceId === null || this.destId === null) return;
    this.wsClient.send({
      type: 'SetTransporterRoute',
      station_building_id: this.stationId,
      source_building_id: this.sourceId,
      dest_building_id: this.destId,
    });
    console.info(`SetTransporterRoute: station=${this.stationId} source=${this.sourceId} dest=${this.destId}`);
    this.hide();
  }
}
